import React, { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Image, Modal, Table, Tag, Tooltip, Typography, message } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { getCustomAvatars, deleteCustomAvatar, AdminAvatarSlot } from '../../../../Actions/adminSlotApi'

type Props = {
  workspaceId: number
}

export default function CustomAvatarSlots({ workspaceId }: Props) {
  const queryClient = useQueryClient()
  const [messageApi, contextHolder] = message.useMessage()

  const queryKey = ['adminCustomAvatars', workspaceId]

  const { data: slots = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getCustomAvatars(workspaceId),
    enabled: !!workspaceId,
  })

  const deleteMutation = useMutation({
    mutationFn: (selfCustomAvatarHistoryId: number) => deleteCustomAvatar(selfCustomAvatarHistoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      messageApi.success('커스텀 아바타 슬롯을 삭제했습니다.')
    },
    onError: (e: Error) => messageApi.error(e.message),
  })

  const handleDelete = useCallback(
    (record: AdminAvatarSlot) => {
      Modal.confirm({
        title: '커스텀 아바타 슬롯 삭제',
        content: `"${record.name || `ID: ${record.selfCustomAvatarHistoryId}`}" 아바타를 삭제하시겠습니까? 워크스페이스에서 즉시 사라지며, 복구는 관리자 DB 작업이 필요합니다.`,
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: () => deleteMutation.mutate(record.selfCustomAvatarHistoryId),
      })
    },
    [deleteMutation],
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'selfCustomAvatarHistoryId',
      key: 'selfCustomAvatarHistoryId',
      width: 70,
    },
    {
      title: '썸네일',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 90,
      render: (url: string | null) =>
        url ? <Image src={url} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null) => text || '-',
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string | null, record: AdminAvatarSlot) => (
        <>
          {status ? <Tag>{status}</Tag> : '-'}
          {record.aivatarId == null ? <Tag color="orange">미완성</Tag> : null}
        </>
      ),
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text: string | null) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '삭제여부',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 160,
      render: (text: string | null) =>
        text ? <Tag color="default">삭제됨 · {dayjs(text).format('YYYY-MM-DD HH:mm')}</Tag> : <Tag color="green">활성</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: AdminAvatarSlot) => {
        if (record.deletedAt) {
          return (
            <Tooltip title="이미 삭제된 슬롯">
              <Button type="text" danger icon={<DeleteOutlined />} disabled />
            </Tooltip>
          )
        }
        // aivatarId 가 null 이면 아직 Aivatar 가 없어 백엔드에서 no-op — 오해 방지 위해 비활성.
        if (record.aivatarId == null) {
          return (
            <Tooltip title="미완성 슬롯(생성 중/실패) — 해제할 리소스가 없습니다">
              <Button type="text" danger icon={<DeleteOutlined />} disabled />
            </Tooltip>
          )
        }
        return <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
      },
    },
  ]

  return (
    <div style={{ marginBottom: 20 }}>
      {contextHolder}
      <Typography.Title level={4}>커스텀 아바타</Typography.Title>
      <Table
        columns={columns}
        dataSource={slots}
        rowKey="selfCustomAvatarHistoryId"
        loading={isLoading}
        pagination={false}
        onRow={(record: AdminAvatarSlot) => (record.deletedAt ? { style: { opacity: 0.5 } } : {})}
        locale={{ emptyText: '커스텀 아바타가 없습니다.' }}
      />
    </div>
  )
}
